import { SCALES, MeasurementSystem, units } from '@seddi/core';
import { LogoTechnique } from '@seddi/scene';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isInside as isLogoInside } from '@/domain/logos/logoBounds';
import { updateGraphicDisplayName, updateGraphicRegion, updateGraphicSize, setGraphicTransform, updateGraphicTechnique, updateGraphicCustomTechnique, } from '@/store/appSlice';
import { getInitialTransformFromRegion } from '../GraphicsPanel/logos/logoPlacement';
import { calculateLogoSizeForRegion } from '../GraphicsPanel/logos/logoSizingStrategies';
const SCALE = SCALES.m;
const MIN_LOGO_SIZE = 0.001;
const SCALE_FACTOR = 1.05;
const buildOptions = (items) => items.map((item, i) => (Object.assign({ id: i }, item)));
export const TECHNIQUE_OPTIONS = buildOptions([
    { value: LogoTechnique.Rubber, label: 'Plastisol' },
    { value: LogoTechnique.Waterbased, label: 'Waterbased' },
    { value: LogoTechnique.Glitter, label: 'Glitter' },
    { value: LogoTechnique.HighDensity, label: 'High Density' },
    { value: LogoTechnique.Puff, label: 'Puff' },
    { value: LogoTechnique.HeatTransfer, label: 'Heat Transfer' },
    { value: LogoTechnique.DirectToGarment, label: 'Direct To Garment' },
    { value: LogoTechnique.Embroidery, label: 'Embroidery' },
    { value: LogoTechnique.Patch, label: 'Patch' },
    { value: LogoTechnique.Custom, label: 'Custom' },
]);
const is2DProduct = false; // TODO: derive from style if needed
export const useGraphicProperties = ({ logo, regionOptions, styleRegions, editableRegions = [], blockBrandLogoRegions = false, units: ms = MeasurementSystem.Imperial, }) => {
    var _a;
    const dispatch = useDispatch();
    const editableRegionIds = useMemo(() => new Set(editableRegions.map(region => region.id)), [editableRegions]);
    const { toDisplay, toUniversal, unitSuffix } = useMemo(() => ({
        toDisplay: (universal) => units.translateToUserUnits(ms, SCALE, universal, 2),
        toUniversal: (display) => units.translateToUniversalUnits(ms, SCALE, display),
        unitSuffix: units.obtainShortUnits(ms, SCALE),
    }), [ms]);
    const [inputValues, setInputValues] = useState(() => {
        var _a, _b;
        return ({
            x: String((_a = toDisplay(logo.size.x)) !== null && _a !== void 0 ? _a : ''),
            y: String((_b = toDisplay(logo.size.y)) !== null && _b !== void 0 ? _b : ''),
        });
    });
    const [focusedAxis, setFocusedAxis] = useState(null);
    const [customTechnique, setCustomTechnique] = useState((_a = logo.customTechnique) !== null && _a !== void 0 ? _a : '');
    useEffect(() => {
        var _a;
        setCustomTechnique((_a = logo.customTechnique) !== null && _a !== void 0 ? _a : '');
    }, [logo.id, logo.customTechnique]);
    useEffect(() => {
        var _a, _b;
        if (focusedAxis !== null)
            return;
        setInputValues({
            x: String((_a = toDisplay(logo.size.x)) !== null && _a !== void 0 ? _a : ''),
            y: String((_b = toDisplay(logo.size.y)) !== null && _b !== void 0 ? _b : ''),
        });
    }, [logo.size.x, logo.size.y, focusedAxis, toDisplay]);
    const handleNameBlur = useCallback((value) => {
        const trimmed = value.trim();
        if (trimmed) {
            dispatch(updateGraphicDisplayName({ id: logo.id, displayName: trimmed }));
        }
    }, [dispatch, logo.id]);
    const handleSizeInputChange = useCallback((axis, value) => {
        setInputValues(prev => (Object.assign(Object.assign({}, prev), { [axis]: value })));
    }, []);
    const commitAxis = useCallback((axis) => {
        var _a, _b, _c, _d;
        const raw = inputValues[axis];
        const numDisplay = parseFloat(raw);
        if (!raw || isNaN(numDisplay) || numDisplay <= 0) {
            setInputValues({
                x: String((_a = toDisplay(logo.size.x)) !== null && _a !== void 0 ? _a : ''),
                y: String((_b = toDisplay(logo.size.y)) !== null && _b !== void 0 ? _b : ''),
            });
            return;
        }
        const universal = toUniversal(numDisplay);
        if (universal < MIN_LOGO_SIZE) {
            setInputValues({
                x: String((_c = toDisplay(logo.size.x)) !== null && _c !== void 0 ? _c : ''),
                y: String((_d = toDisplay(logo.size.y)) !== null && _d !== void 0 ? _d : ''),
            });
            return;
        }
        const ratio = logo.size.x / logo.size.y;
        const otherAxis = axis === 'x' ? 'y' : 'x';
        const otherUniversal = axis === 'x' ? universal / ratio : universal * ratio;
        dispatch(updateGraphicSize({
            id: logo.id,
            size: {
                x: axis === 'x' ? universal : otherUniversal,
                y: axis === 'y' ? universal : otherUniversal,
            },
        }));
        setInputValues(prev => {
            var _a;
            return (Object.assign(Object.assign({}, prev), { [otherAxis]: String((_a = toDisplay(otherUniversal)) !== null && _a !== void 0 ? _a : '') }));
        });
    }, [dispatch, logo.id, logo.size, inputValues, toDisplay, toUniversal]);
    const onInputFocus = useCallback((axis) => {
        setFocusedAxis(axis);
    }, []);
    const onInputFocusOut = useCallback((axis) => {
        setFocusedAxis(null);
        commitAxis(axis);
    }, [commitAxis]);
    const scaleSize = useCallback((op) => {
        const multiplier = op === 'increase' ? SCALE_FACTOR : 1 / SCALE_FACTOR;
        const newSize = {
            x: logo.size.x * multiplier,
            y: logo.size.y * multiplier,
        };
        dispatch(updateGraphicSize({ id: logo.id, size: newSize }));
    }, [dispatch, logo.id, logo.size]);
    const handleRegionChange = useCallback((e) => {
        var _a;
        const value = (_a = e === null || e === void 0 ? void 0 : e.value) !== null && _a !== void 0 ? _a : String(e);
        const region = styleRegions.find(r => r.id === value);
        if (blockBrandLogoRegions && !editableRegionIds.has(value)) {
            return;
        }
        if (!region) {
            dispatch(updateGraphicRegion({ id: logo.id, region: value }));
            return;
        }
        const imageSizeFitted = calculateLogoSizeForRegion(logo.imageSize, region, is2DProduct);
        const transform = getInitialTransformFromRegion(region, imageSizeFitted, is2DProduct, logo.transform.rotation);
        dispatch(updateGraphicRegion({ id: logo.id, region: value }));
        dispatch(updateGraphicSize({ id: logo.id, size: imageSizeFitted }));
        dispatch(setGraphicTransform({ id: logo.id, transform }));
    }, [
        dispatch,
        logo.id,
        logo.imageSize,
        logo.transform.rotation,
        styleRegions,
        blockBrandLogoRegions,
        editableRegionIds,
    ]);
    const handleTechniqueChange = useCallback((e) => {
        var _a;
        const value = (_a = e === null || e === void 0 ? void 0 : e.value) !== null && _a !== void 0 ? _a : String(e);
        dispatch(updateGraphicTechnique({ id: logo.id, technique: value }));
    }, [dispatch, logo.id]);
    const handleCustomTechniqueBlur = useCallback(() => {
        dispatch(updateGraphicCustomTechnique({ id: logo.id, customTechnique }));
    }, [dispatch, logo.id, customTechnique]);
    const showOutsideWarn = useMemo(() => {
        if (!logo || !styleRegions.length)
            return false;
        const region = styleRegions.find(r => r.id === logo.region);
        if (!region)
            return false;
        return !isLogoInside(logo.size, logo.transform, region.size, region.transform);
    }, [logo, styleRegions]);
    return {
        customTechnique,
        handleCustomTechniqueBlur,
        handleNameBlur,
        handleRegionChange,
        handleSizeInputChange,
        handleTechniqueChange,
        inputValues,
        onInputFocus,
        onInputFocusOut,
        regionOptions,
        setCustomTechnique,
        scaleSize,
        showOutsideWarn,
        unitSuffix,
    };
};
